import ApiCache from "@/components/interceptor/ApiCache";
import { Toast } from "@/components/toasts";
import { ModalProvider } from "@/contexts/ModalContext";
import { fetchApi } from "@/lib";
import { getStoredCompany } from "@/lib/company-storage";
import { ensureCompanyForCurrentHost } from "@/lib/ensure-company";
import { ApiData } from "@/types";
import { useCallback, useEffect, useState } from "react";

import { apiMerchantPublic } from "./api";
import merchantAccessStateAtom from "./atoms/merchant-access";
import { TableContext, getStoredTable, persistTableContext, tableAtom } from "./atoms/table";
import { initMetaPixelForMerchant } from "./lib/meta-pixel";
import { captureAttribution } from "./domain/attribution/attribution";
import { useSetAtom } from "jotai";
import { CartInitializer } from "./components/home/CartInitializer";
import { FlyToCartLayer } from "./components/cart/FlyToCartLayer";
import { useActiveOrders } from "./hooks/use-active-orders";
import { useAppVersionRefresh } from "./hooks/use-app-version-refresh";
import { useApiPolling } from "./hooks/use-api-polling";
import { useBodyScrollUnlock } from "./hooks/use-body-scroll-unlock";
import { useCartTtlGuard } from "./hooks/use-cart-ttl-guard";
import { useCartCatalogGuard } from "./hooks/use-cart-catalog-guard";
import { useDeliveryAreaConfig } from "./hooks/use-delivery-area-config";
import { useOptimisticCart } from "./hooks/use-optimistic-cart";
import { useSchedulingCatalog } from "./hooks/use-scheduling-catalog";
import { useSuggestionEventsFlush } from "./hooks/use-suggestion-events-flush";
import { useStorefrontAnalyticsFlush } from "./hooks/use-storefront-analytics-flush";
import { useSchedulingContext } from "./hooks/use-scheduling-context";
import { useStorefrontColors } from "./hooks/use-storefront-colors";
import {
  loadStorefrontAppearance,
  settleStorefrontAppearance,
} from "./lib/load-storefront-appearance";
import { useAccountTokenRenewal } from "./hooks/use-account-token-renewal";
import { useUserProfile } from "./hooks/use-user-profile";
import { LoadingPage } from "./loading";
import { ErrorPage } from "./pages/error";
import { StoreNotFoundError, StoreNotFoundPage } from "./pages/error/StoreNotFound";
import { Router } from "./router";

/**
 * Storefront COLORS, mounted outside bootstrap branches.
 *
 * Lived inside `HydratedShell` and could not: that component only exists after
 * merchant and catalog load, and BEFORE that `LoadingPage` draws a skeleton-only
 * screen. With the hook there, loading was the only screen without a known
 * palette — and the skeleton waiting on it painted nothing.
 *
 * Only PAINTS: `loadStorefrontAppearance` fetches outside React so it can start
 * earlier. Renders `null` — an effect with a place in the tree, not a screen.
 */
function StorefrontTheme() {
  useStorefrontColors();
  return null;
}

function HydratedShell() {
  useBodyScrollUnlock();
  useOptimisticCart();
  useCartTtlGuard();
  useCartCatalogGuard();
  useAppVersionRefresh();
  useUserProfile();
  useAccountTokenRenewal();
  useActiveOrders();
  useSchedulingContext(true);
  useSchedulingCatalog();
  useDeliveryAreaConfig(true);
  useSuggestionEventsFlush();
  useStorefrontAnalyticsFlush();

  return (
    <>
      <CartInitializer />
      <Router />
      <FlyToCartLayer />
    </>
  );
}

function App() {
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const setTableCtx = useSetAtom(tableAtom);
  const setMerchantAccessState = useSetAtom(merchantAccessStateAtom);

  const handleDataUpdate = useCallback((newData: ApiData | null) => {
    if (newData) setData(newData)
  }, [])

  useApiPolling(handleDataUpdate, !loading && !error);

  useEffect(() => {
    async function loadData() {
      try {
        await loadCompanyData();
        void loadStorefrontAppearance();
        await loadTableContext();
        void initMetaPixelForMerchant(getStoredCompany()?.id);
        captureAttribution(getStoredCompany()?.id);
        const apiData = await fetchApi();
        setData(apiData);
      } catch (err) {
        setError(err as Error);
      } finally {
        settleStorefrontAppearance();
        setLoading(false);
      }
    }

    async function loadTableContext() {
      const tableId = new URLSearchParams(window.location.search).get("table");
      if (!tableId) {
        persistTableContext(null);
        setTableCtx(null);
        return;
      }
      const company = getStoredCompany();
      if (!company) return;

      const cached = getStoredTable();
      const seed: TableContext =
        cached?.id === tableId ? cached : { id: tableId, name: "" };
      persistTableContext(seed);
      setTableCtx(seed);

      try {
        const { data: tableData } = await apiMerchantPublic.get<TableContext>(
          `/public/merchants/${company.id}/tables/${tableId}`,
        );
        if (tableData?.id !== tableId) {
          console.warn("[table] lookup returned another table; keeping the QR table id", {
            requested: tableId,
            received: tableData?.id,
          });
          return;
        }
        persistTableContext(tableData);
        setTableCtx(tableData);
      } catch (err) {
        console.warn(
          "[table] name lookup failed; staying in table mode without a resolved name",
          err,
        );
      }
    }

    async function loadCompanyData() {
      const result = await ensureCompanyForCurrentHost({ forceRefresh: true });
      if (result.company) {
        setMerchantAccessState(result.company.accessState ?? null);
        if (result.error) {
          console.warn(
            "Failed to revalidate company; keeping cached/healed company",
            result.error,
          );
        }
        return;
      }

      const subdomain =
        import.meta.env.VITE_DEV_MODE === "developer"
          ? "developer"
          : window.location.hostname.split(".")[0];

      if (
        result.error &&
        typeof result.error === "object" &&
        result.error !== null &&
        "response" in result.error &&
        (result.error as { response?: { status?: number } }).response?.status === 404
      ) {
        throw new StoreNotFoundError(subdomain);
      }

      console.error("Erro ao carregar dados da empresa:", result.error);
      throw result.error instanceof Error
        ? result.error
        : new Error("Failed to load storefront company");
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StorefrontTheme />
      <BootstrapScreen loading={loading} error={error} data={data} />
    </>
  );
}

/** Which of the four bootstrap screens is showing. */
function BootstrapScreen({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: Error | null;
  data: ApiData | null;
}) {
  if (loading) return <LoadingPage />;
  if (error instanceof StoreNotFoundError) return <StoreNotFoundPage />;
  if (error) return <ErrorPage error={error} />;

  return (
    <ModalProvider>
      <ApiCache data={data}>
        <HydratedShell />
      </ApiCache>
      <Toast />
    </ModalProvider>
  );
}

export default App;
